/**
 * A custom element that manages the account login actions.
 *
 * @extends {HTMLElement}
 */
class AccountLoginActions extends HTMLElement {
  /**
   * @type {Element | null}
   */
  shopLoginButton = null;

  /**
   * @type {AbortController | null}
   */
  #abortController = null;

  /**
   * @type {boolean}
   */
  #isHydrated = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#setupActivationListeners();
    this.#setupShopLoginButton();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  hydrate = () => {
    if (this.#isHydrated) return;

    const template = this.querySelector("[data-shop-login-template]");
    const target = this.querySelector("[data-shop-login-target]");

    if (
      !(template instanceof HTMLTemplateElement) ||
      !(target instanceof HTMLElement)
    ) {
      this.#setupShopLoginButton();
      return;
    }

    const fragment = /** @type {DocumentFragment} */ (
      template.content.cloneNode(true)
    );

    this.#activateScripts(fragment);
    target.replaceChildren(fragment);
    template.remove();

    this.#isHydrated = true;
    this.#setupShopLoginButton();
  };

  #setupActivationListeners() {
    const { signal } = this.#abortController ?? new AbortController();
    const details = this.closest("details");
    const dialogComponent = this.closest("dialog-component");
    const trigger =
      details?.querySelector("summary") ??
      dialogComponent?.querySelector(".account-button");

    trigger?.addEventListener("pointerenter", this.hydrate, {
      once: true,
      signal,
    });
    trigger?.addEventListener("focusin", this.hydrate, { once: true, signal });
    trigger?.addEventListener("click", this.hydrate, { once: true, signal });

    details?.addEventListener(
      "toggle",
      () => {
        if (details.open) this.hydrate();
      },
      { signal },
    );

    dialogComponent?.addEventListener("dialog:open", this.hydrate, { signal });

    this.addEventListener("focusin", this.hydrate, { once: true, signal });
    this.addEventListener("pointerenter", this.hydrate, { once: true, signal });
  }

  /**
   * @param {DocumentFragment} fragment
   */
  #activateScripts(fragment) {
    for (const script of fragment.querySelectorAll("script")) {
      const liveScript = document.createElement("script");

      for (const { name, value } of script.attributes) {
        liveScript.setAttribute(name, value);
      }

      if (script.textContent) {
        liveScript.textContent = script.textContent;
      }

      script.replaceWith(liveScript);
    }
  }

  #setupShopLoginButton() {
    this.shopLoginButton = this.querySelector("shop-login-button");

    if (!this.shopLoginButton) return;

    this.#isHydrated = true;

    // We don't have control over the shop-login-button markup, so we need to set additional attributes here
    this.shopLoginButton.setAttribute("full-width", "true");
    this.shopLoginButton.setAttribute("persist-after-sign-in", "true");
    // Do this only if New Customer Account is ALWAYS the sign in option (and never Classic Customer Account)
    this.shopLoginButton.setAttribute(
      "analytics-context",
      "loginWithShopSelfServe",
    );
    this.shopLoginButton.setAttribute(
      "flow-version",
      "account-actions-popover",
    );
    this.shopLoginButton.setAttribute("return-uri", window.location.href);

    if (this.shopLoginButton.hasAttribute("data-reload-on-complete")) return;

    // Reload the page after the login is completed, otherwise the page state is incorrect
    this.shopLoginButton.setAttribute("data-reload-on-complete", "true");
    this.shopLoginButton.addEventListener("completed", () => {
      window.location.reload();
    });
  }
}

if (!customElements.get("account-login-actions")) {
  customElements.define("account-login-actions", AccountLoginActions);
}
